import {
  DefaultModalScreenOptions,
  ModalPath,
  ScreenPath,
} from "@/constants/Navigation";
import { Stack } from "@/crossPlatform/expo-router";
import { useAuthenticatedAppProviders } from "@/hooks/useAppProviders";
import { useIsRootNavigatorReadySelector } from "@/store/reducers/appStartup";
import { useBookingDispatch } from "@/store/reducers/booking";
import { useGroupClassDetailsModalDispatch } from "@/store/reducers/modals/groupClassDetailsModal";
import { useRecommendedGroupClassModalDispatch } from "@/store/reducers/modals/recommendedGroupClassModal";
import { useScheduleEventDetailsModalDispatch } from "@/store/reducers/modals/scheduleEventDetailsModal";
import EquipSplashScreen from "@/ui/components/splashScreen/EquipSplashScreen";

const Layout = () => {
  const { resetBookingState } = useBookingDispatch();
  const { resetGroupClassDetailsData } = useGroupClassDetailsModalDispatch();
  const { resetRecommendedGroupClassModal } =
    useRecommendedGroupClassModalDispatch();
  const { resetScheduleEventDetailsData } =
    useScheduleEventDetailsModalDispatch();
  const isRootNavigatorReady = useIsRootNavigatorReadySelector();
  const AuthenticatedAppProviders = useAuthenticatedAppProviders();
  // during app/RootNavigator initialization, isAuthenticated may be set to true if biometic auth is enabled
  // since expo routes here at app startup, that may cause a brief flicker of trying to render the MainTabs here
  // so we wait for isRootNavigatorReady since RootNavigator will route away (to Login, or PostLoginRouting)
  // after it sets isAuthenticated === true, but before it sets isRootNavigatorReady === true.
  // this wait here prevents Tabs rendering here prematurely, instead allowing the RootNavigator-driven routing to take over fully

  // on web, also gate on postLoginRouting completion as a direct URL navigation otherwise races on this gate opening/postLoginRouting running
  // which results in the direct URL rendering briefly, then getting wiped by the replace to /home inexplicably
  if (!isRootNavigatorReady) {
    // unexpectedly (or unfortunately) at the very immediate opening of the app, expo detects a '(tabs)' directory/TabLayout component and begins loading and rendering
    // the app/ directory recursively until it gets there: ie, app/_layout.tsx -> app/(app)/_layout.tsx -> app/(app)/(tabs)/_layout.tsx
    // (eg, if you were to move app/(app)/(tabs) to **anywhere *else, like app/(login)/(tabs), you would see
    // app/_layout.tsx -> app/(login)/_layout.tsx -> app/(login)/(tabs)/_layout.tsx rendered instead the instant the app was opened)

    // expo ALSO begins loading and rendering every Stack.Screen in TabLayout which, at the very instant the app is opened, will cause errors
    // because 1) the user is likely not authenticated or logged in, 2) first time app opening initialization is not complete

    // this early return is absolutely critical to ensure that expo doesn't just try to load TabLayout the instant the app opens
    // instead, we display an AppStartupSplashScreen, also return one here to cover any gaps, and RootNavigator will eventually route to the Login page
    // only once all app initialization is complete and ready
    return <EquipSplashScreen />;
  }

  return (
    <AuthenticatedAppProviders>
      <Stack initialRouteName={ScreenPath.MainTabs}>
        <Stack.Screen
          name={ScreenPath.MainTabs}
          options={{
            // eslint-disable-next-line @typescript-eslint/naming-convention
            headerShown: false,
          }}
        />
        <Stack.Screen
          listeners={{
            beforeRemove: () => {
              resetBookingState();
            },
          }}
          name={ModalPath.BookAppointment}
          options={DefaultModalScreenOptions}
        />
        <Stack.Screen
          listeners={{
            beforeRemove: () => {
              resetGroupClassDetailsData();
            },
          }}
          name={ModalPath.GroupClassListDetails}
          options={DefaultModalScreenOptions}
        />
        <Stack.Screen
          listeners={{
            beforeRemove: () => {
              resetScheduleEventDetailsData();
            },
          }}
          name={ModalPath.ScheduleEventDetails}
          options={DefaultModalScreenOptions}
        />
        <Stack.Screen
          name={ModalPath.GroupClassList}
          options={DefaultModalScreenOptions}
        />
        <Stack.Screen
          name={ModalPath.WeightHistory}
          options={DefaultModalScreenOptions}
        />
        <Stack.Screen
          name={ModalPath.AssessmentTask}
          options={DefaultModalScreenOptions}
        />
        <Stack.Screen
          name={ModalPath.AssessmentWebView}
          options={DefaultModalScreenOptions}
        />
        <Stack.Screen
          name={ModalPath.MedicalEvaluationTask}
          options={DefaultModalScreenOptions}
        />
        <Stack.Screen
          name={ModalPath.SignFormsTask}
          options={DefaultModalScreenOptions}
        />
        <Stack.Screen
          name={ModalPath.LogWeightTask}
          options={DefaultModalScreenOptions}
        />
        <Stack.Screen
          name={ModalPath.LabRequest}
          options={DefaultModalScreenOptions}
        />
        <Stack.Screen
          name={ModalPath.UploadInsuranceTask}
          options={DefaultModalScreenOptions}
        />
        <Stack.Screen name="(onboarding)" options={DefaultModalScreenOptions} />
        <Stack.Screen
          name={ModalPath.OnboardingV2}
          options={DefaultModalScreenOptions}
        />
        <Stack.Screen
          name={ModalPath.EditProfile}
          options={DefaultModalScreenOptions}
        />
        <Stack.Screen
          name={ModalPath.EditNotificationSettings}
          options={DefaultModalScreenOptions}
        />
        <Stack.Screen
          name={ModalPath.Journal}
          options={DefaultModalScreenOptions}
        />
        <Stack.Screen
          name={ModalPath.VideoContent}
          options={DefaultModalScreenOptions}
        />
        <Stack.Screen
          listeners={{
            beforeRemove: () => resetRecommendedGroupClassModal(),
          }}
          name={ModalPath.RecommendedGroupClass}
          options={DefaultModalScreenOptions}
        />
        <Stack.Screen
          name={ModalPath.WaitingRoom}
          options={DefaultModalScreenOptions}
        />
      </Stack>
    </AuthenticatedAppProviders>
  );
};

// overrides expo's default of resolving to the first-alphabetical screen (eg assessment-task) when no route is explicit
const unstable_settings = {
  initialRouteName: "(tabs)",
};

// eslint-disable-next-line import-x/no-unused-modules -- Layout file is used by expo-router file based navigation but TypeScript can not infer its usage
export default Layout;

// eslint-disable-next-line import-x/no-unused-modules -- used by expo-router's static route-tree builder
export { unstable_settings };
